import type { StreamParser, StringStream } from '@codemirror/language';
import { html as htmlParser } from '@codemirror/legacy-modes/mode/xml';

interface State {
  inMustacheTag: boolean;
  htmlState: unknown;
}

/**
 * Returns true if the rest of the current line (from current position) contains
 * a `}}` closing delimiter. Used to enforce the spec rule: multi-line tags are
 * not recognised as tags — they fall back to plain text.
 */
function hasClosingOnLine(stream: StringStream): boolean {
  return stream.string.slice(stream.pos).includes('}}');
}

/**
 * Try to consume a complete comment tag. Returns 'comment' on success, null
 * after advancing one char (the `{`) when the comment doesn't close on the
 * line — caller falls through to the html delegate, which is harmless because
 * the next `{` on its own carries no Mustache meaning.
 */
function tokenizeComment(stream: StringStream): string | null {
  if (stream.match(/^\{\{!--/, false)) {
    if (stream.match(/^\{\{!--.*?--\}\}/)) {
      return 'comment';
    }
    stream.next();
    return null;
  }
  if (stream.match(/^\{\{!.*?\}\}/)) {
    return 'comment';
  }
  stream.next();
  return null;
}

/**
 * Open a tag whose closing `}}` lies on the same line. Consumes the opening
 * delimiter, sets `state.inMustacheTag = true`, and returns the token type.
 * When there is no closing on the line, advances one character so the next
 * call can re-evaluate (and falls back to the html delegate naturally).
 */
function openTag(
  stream: StringStream,
  state: State,
  pattern: RegExp,
  tokenType: string,
): string | null {
  if (!hasClosingOnLine(stream)) {
    stream.next();
    return null;
  }
  stream.match(pattern);
  state.inMustacheTag = true;
  return tokenType;
}

/**
 * Mustache opener dispatch. Caller has already verified the stream is at `{{`.
 */
function tokenizeMustacheOpener(
  stream: StringStream,
  state: State,
): string | null {
  if (stream.match(/^\{\{!/, false)) {
    return tokenizeComment(stream);
  }
  // Set-delimiter `{{= ... =}}` — entire run is plain text, not a tag.
  if (stream.match(/^\{\{=/, false)) {
    if (!stream.match(/^\{\{=.*?=\}\}/)) {
      stream.next();
    }
    return null;
  }
  if (stream.match(/^\{\{[#^/]/, false)) {
    return openTag(stream, state, /^\{\{[#^/]/, 'keyword');
  }
  if (stream.match(/^\{\{>/, false)) {
    return openTag(stream, state, /^\{\{>/, 'keyword');
  }
  if (stream.match(/^\{\{\{/, false)) {
    return openTag(stream, state, /^\{\{\{/, 'punctuation');
  }
  if (stream.match(/^\{\{&/, false)) {
    return openTag(stream, state, /^\{\{&/, 'punctuation');
  }
  return openTag(stream, state, /^\{\{/, 'punctuation');
}

function tokenizeInsideMustacheTag(
  stream: StringStream,
  state: State,
): string | null {
  if (stream.eatSpace()) {
    return null;
  }
  if (stream.match(/^\}\}\}/) || stream.match(/^\}\}/)) {
    state.inMustacheTag = false;
    return 'punctuation';
  }
  // Implicit iterator `.` (when not followed by an identifier char).
  if (stream.match(/^\.(?![\w.])/)) {
    return 'variableName';
  }
  // Identifier — allow dotted names (`a.b.c`) as one token.
  if (stream.match(/^[\w]+(?:\.[\w]+)*/)) {
    return 'variableName';
  }
  // Anything else inside the tag — text.
  stream.next();
  return null;
}

/**
 * Delegate one token to the html parser, but mask any upcoming `{{` so the
 * html parser can never read past a Mustache opener (its text/comment/CDATA
 * sub-tokenizers all do `eatWhile` past arbitrary characters and would
 * otherwise swallow the `{{` as plain text). We temporarily slice the
 * StringStream's `.string` to end at the next `{{`, run the html parser, and
 * restore the full line afterwards.
 */
function tokenizeWithHtml(stream: StringStream, state: State): string | null {
  const next = stream.string.indexOf('{{', stream.pos);
  if (next === -1 || next === stream.pos) {
    return htmlParser.token(stream, state.htmlState as never) as string | null;
  }
  const fullString = stream.string;
  const mutable = stream as { string: string };
  mutable.string = fullString.slice(0, next);
  try {
    return htmlParser.token(stream, state.htmlState as never) as string | null;
  } finally {
    mutable.string = fullString;
  }
}

function copyHtmlState(s: unknown): unknown {
  const cs = (htmlParser as { copyState?: (s: unknown) => unknown }).copyState;
  if (typeof cs === 'function') {
    return cs(s);
  }
  // Fallback: shallow-clone the legacy state object. The Context linked list
  // is treated as immutable by the xml mode (popContext rebinds the field, it
  // never mutates a node), so sharing it is safe.
  return s && typeof s === 'object' ? { ...(s as object) } : s;
}

/**
 * Mustache StreamParser layered on top of the legacy html mode, so HTML tags
 * around `{{...}}` placeholders also receive syntax highlighting. The mode
 * supports:
 *
 * - `{{!comment}}` and `{{!-- comment --}}` → comment
 * - `{{#section}}`, `{{^inverted}}`, `{{/section}}`, `{{>partial}}` → keyword
 *   opener + variableName + punctuation closer
 * - `{{var}}`, `{{{unescaped}}}`, `{{&unescaped}}` → punctuation + variableName
 *   + punctuation
 * - Whitespace inside delimiters, dotted names (`a.b.c`) and the implicit
 *   iterator (`.`)
 * - Multi-line tags and set-delimiter tags (`{{=<% %>=}}`) → plain text
 *
 * Everything outside `{{...}}` is delegated to the html parser, so HTML tags,
 * attributes, attribute values and angle brackets pick up their own styles
 * from the shared HighlightStyle in CodeEditor.theme.ts.
 */
export const mustache: StreamParser<State> = {
  startState: () => ({
    inMustacheTag: false,
    htmlState: htmlParser.startState
      ? htmlParser.startState(2)
      : ({} as unknown),
  }),
  copyState: (s) => ({
    inMustacheTag: s.inMustacheTag,
    htmlState: copyHtmlState(s.htmlState),
  }),

  token(stream, state) {
    if (state.inMustacheTag) {
      return tokenizeInsideMustacheTag(stream, state);
    }
    if (stream.string.startsWith('{{', stream.pos)) {
      return tokenizeMustacheOpener(stream, state);
    }
    return tokenizeWithHtml(stream, state);
  },
};
